import { type ReactNode, isValidElement } from 'react';
import type {
  Article,
  DefinedTerm,
  FAQPage,
  Person,
  Question,
  SpeakableSpecification,
  VideoObject,
  WebPage,
  WebSite,
} from 'schema-dts';

import { generalConfig } from '@sola/config/lib/getters.ts';
import type { PageResponseDto } from '@sola/schema/lib/contentful.ts';
import type { ArticleProps, FundProps } from '@sola/schema/lib/schema.ts';
import { toIsoString } from '@sola/utils/date.ts';

import { getOptimizedImageUrl } from './image-utils.ts';

export const organizationId = `${generalConfig.domain.compassion}/#Organization`;
export const webSiteId = `${generalConfig.domain.compassion}/#WebSite`;

const ensureAbsoluteUrl = (url?: string | null): string | undefined => {
  if (!url || url.trim() === '') {
    return undefined;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `${generalConfig.domain.compassion}${url.startsWith('/') ? url : `/${url}`}`;
};

const getSchemaImageUrl = (
  imageUrl?: string | null,
  width = 1200,
  quality = 80,
): string | undefined => {
  if (!imageUrl || imageUrl.trim() === '') {
    return undefined;
  }

  const optimizedUrl = getOptimizedImageUrl(imageUrl, width, quality);
  return ensureAbsoluteUrl(optimizedUrl ?? imageUrl);
};

export type BuildVideoInput = {
  name?: string | null;
  description?: string | null;
  thumbnailUrl?: string | string[] | null;
  contentUrl?: string | null;
  embedUrl?: string | null;
  duration?: string | null;
  uploadDate?: string | null;
};

type WebPageInput = {
  name?: string | null;
  description?: string | null;
  url?: string | null;
  inLanguage?: string | string[] | null;
  datePublished?: string | Date | null;
  image?: string | null;
};

export type FaqItemInput = {
  question?: string | null;
  answer?: ReactNode | null;
};

const isEmptyString = (v: unknown): v is '' =>
  typeof v === 'string' && v.trim() === '';

export const cleanObject = <T extends Record<string, unknown>>(obj: T): T => {
  const result = Object.entries(obj).reduce<Record<string, unknown>>(
    (acc, [k, v]) => {
      if (v === undefined || v === null || isEmptyString(v)) {
        return acc;
      }

      if (Array.isArray(v)) {
        const arr = v
          .map((it) =>
            it !== null && typeof it === 'object' && !(it instanceof Date)
              ? cleanObject(it as Record<string, unknown>)
              : it,
          )
          .filter(
            (it) => !(it === undefined || it === null || isEmptyString(it)),
          );
        if (arr.length) {
          acc[k] = arr;
        }
        return acc;
      }

      if (typeof v === 'object' && !(v instanceof Date)) {
        const o = cleanObject(v as Record<string, unknown>);
        if (Object.keys(o).length) {
          acc[k] = o;
        }
        return acc;
      }

      acc[k] = v as unknown;
      return acc;
    },
    {},
  );
  return result as T;
};

export const reactNodeToText = (node: ReactNode): string => {
  if (node == null || node === false) {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(reactNodeToText).join(' ');
  }
  if (isValidElement(node)) {
    const props = node.props;
    const children =
      typeof props === 'object' && props !== null && 'children' in props
        ? (props.children as ReactNode)
        : undefined;
    return reactNodeToText(children);
  }
  return '';
};

export const buildVideoJsonLd = (
  input: BuildVideoInput,
): Partial<VideoObject> => {
  const rawThumbnailUrl =
    typeof input.thumbnailUrl === 'string'
      ? [input.thumbnailUrl]
      : (input.thumbnailUrl ?? undefined);

  const thumbnailUrl = rawThumbnailUrl
    ?.map((url) => getSchemaImageUrl(url, 1200, 75))
    .filter((url): url is string => url !== undefined);

  const uploadDate =
    input.uploadDate == null ? undefined : toIsoString(input.uploadDate);

  const timeRequired =
    input.duration && input.duration.trim() !== '' ? input.duration : undefined;

  return cleanObject<Partial<VideoObject>>({
    name: input.name ?? undefined,
    description: input.description ?? undefined,
    thumbnailUrl: thumbnailUrl?.length ? thumbnailUrl : undefined,
    contentUrl: ensureAbsoluteUrl(input.contentUrl),
    embedUrl: ensureAbsoluteUrl(input.embedUrl),
    duration: input.duration ?? undefined,
    timeRequired,
    uploadDate,
  });
};

/**
 * Map a Contentful article taxonomy type to the most specific schema.org type.
 * Blog posts use `BlogPosting`; everything else falls back to `Article`.
 */
export const getArticleSchemaType = (
  taxonomyArticleType?: string | null,
): 'BlogPosting' | 'Article' =>
  taxonomyArticleType === 'blogPost' ? 'BlogPosting' : 'Article';

export const buildArticleJsonLd = (
  article: ArticleProps,
  path: string,
): Partial<Article> => {
  const primaryImageUrl =
    article.primaryMedia?.items?.[0]?.media?.image?.url ??
    article.primaryMedia?.items?.[0]?.mediaLogo?.image?.url ??
    generalConfig.publisher.logoUrl;

  const first = article.contributors?.[0];

  const authorImageUrl = getSchemaImageUrl(first?.image?.url, 400, 80);

  const author = first
    ? {
        '@type': 'Person',
        name: first.name,
        url: generalConfig.publisher.url,
        image: authorImageUrl
          ? { '@type': 'ImageObject', url: authorImageUrl }
          : undefined,
      }
    : {
        '@type': 'Organization',
        name: 'Compassion Team',
        url: generalConfig.publisher.url,
      };

  const optimizedPrimaryImageUrl = getSchemaImageUrl(primaryImageUrl, 1200, 80);

  return cleanObject<Partial<Article>>({
    headline: article.title,
    description: article.summary,
    datePublished: toIsoString(article.publishDate),
    dateModified: toIsoString(article.modifiedDate),

    author: author as unknown as Article['author'],
    mainEntityOfPage: { '@type': 'WebPage' },

    publisher: {
      '@id': organizationId,
      '@type': 'Organization',
      name: generalConfig.publisher.name,
      logo: {
        '@type': 'ImageObject',
        url: ensureAbsoluteUrl(generalConfig.publisher.logoUrl),
      },
    } as unknown as Article['publisher'],

    image: optimizedPrimaryImageUrl ?? ensureAbsoluteUrl(primaryImageUrl),

    url: `${generalConfig.domain.compassion}${path}`,
  });
};

export const buildWebPageJsonLd = (input?: WebPageInput): Partial<WebPage> => {
  const datePublished =
    input?.datePublished instanceof Date
      ? input.datePublished.toISOString()
      : (input?.datePublished ?? undefined);

  const inLanguage = Array.isArray(input?.inLanguage)
    ? input.inLanguage.filter(Boolean).join(', ')
    : (input?.inLanguage ?? undefined);

  const url =
    input?.url ??
    (globalThis.window === undefined ? undefined : globalThis.location.href);

  const optimizedImageUrl = getSchemaImageUrl(input?.image, 1200, 80);

  return cleanObject<Partial<WebPage>>({
    name: input?.name ?? undefined,
    description: input?.description ?? undefined,
    url,
    inLanguage,
    datePublished,
    image: optimizedImageUrl ?? ensureAbsoluteUrl(input?.image),
  });
};

export const buildWebPageJsonLdFromPage = (
  data: PageResponseDto,
  options: { url?: string } = {},
): Partial<WebPage> => {
  const optimizedShareImageUrl = getSchemaImageUrl(
    data.shareImage?.image?.url,
    1200,
    80,
  );

  return cleanObject<Partial<WebPage>>({
    mainEntity: {
      '@type': 'Person',
      name: data.pageTitle ?? data.seoTitle ?? data.slug ?? undefined,
      url: options.url ?? data.path ?? undefined,
      image:
        optimizedShareImageUrl ??
        ensureAbsoluteUrl(data.shareImage?.image?.url),
      description:
        data.seoMetaDescription ??
        data.summary ??
        data.shareDescription ??
        undefined,
    } as Person,
  });
};

export const buildFaqPageJsonLd = (
  items: FaqItemInput[],
  options: { pageUrl?: string } = {},
): Partial<FAQPage> => {
  const questions: Question[] = items
    .filter((i) => (i.question ?? '').toString().trim() && i.answer != null)
    .map((i) => ({
      '@type': 'Question',
      name: (i.question ?? '').toString().trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: reactNodeToText(i.answer!).trim() || 'No answer provided.',
      },
    }));

  if (!questions.length) {
    return {};
  }

  // When the owning page URL is known, link the FAQ node into the page graph
  // so crawlers associate it with the WebPage rather than treating it as an
  // orphan node.
  const linkage = options.pageUrl
    ? {
        '@id': `${options.pageUrl}#FAQPage`,
        isPartOf: { '@id': `${options.pageUrl}#WebPage` },
      }
    : {};

  return {
    ...linkage,
    mainEntity: questions as FAQPage['mainEntity'],
  } as Partial<FAQPage>;
};

export const buildOrganizationJsonLd = (): Record<string, unknown> => {
  const optimizedLogoUrl =
    getSchemaImageUrl(generalConfig.publisher.logoUrl, 600, 85) ??
    ensureAbsoluteUrl(generalConfig.publisher.logoUrl);
  const optimizedHomepageImageUrl =
    getSchemaImageUrl(generalConfig.images.homepage, 1200, 80) ??
    ensureAbsoluteUrl(generalConfig.images.homepage);

  return cleanObject({
    '@id': organizationId,
    name: generalConfig.publisher.name,
    alternateName: ['Compassion'],
    url: generalConfig.publisher.url,
    description:
      "Through sponsorship, you can release a child from poverty in Jesus' name. Help us put a stop to child poverty by empowering local churches to care for children in need.",
    logo: {
      '@type': 'ImageObject',
      url: optimizedLogoUrl,
      contentUrl: optimizedLogoUrl,
      name: `${generalConfig.publisher.name} - Logo`,
    },
    image: {
      '@type': 'ImageObject',
      url: optimizedHomepageImageUrl,
      contentUrl: optimizedHomepageImageUrl,
      name: generalConfig.publisher.name,
    },
    telephone: generalConfig.contact.telephone,
    sameAs: Object.values(generalConfig.socials),
    areaServed: generalConfig.contact.areaServed,
    address: {
      '@type': 'PostalAddress',
      ...generalConfig.address,
    },
  });
};

export const buildWebSiteJsonLd = (): Partial<WebSite> =>
  cleanObject<Partial<WebSite>>({
    '@id': webSiteId,
    name: generalConfig.publisher.name,
    alternateName: 'Compassion',
    url: generalConfig.publisher.url,
    description:
      "Compassion International shares the hope of the gospel in word and action, releasing children from poverty in Jesus' name.",
    publisher: { '@id': organizationId },
  } as Partial<WebSite>);

type WebPageNodeInput = {
  url: string;
  name?: string | null;
  description?: string | null;
  inLanguage?: string | null;
  datePublished?: string | Date | null;
  dateModified?: string | Date | null;
  image?: string | null;
  /** When true, attach a SpeakableSpecification pointing at the page title and meta description. */
  speakable?: boolean;
  /**
   * When the page renders a breadcrumb, pass the same canonical URL used for
   * the BreadcrumbList `@id` so the WebPage can reference it. The reference is
   * built as `${breadcrumbUrl}#BreadcrumbList`.
   */
  breadcrumbUrl?: string | null;
  /** Entity keywords (e.g. DefinedTerm nodes) to attach to the page. */
  keywords?: DefinedTerm[];
};

export const buildWebPageNode = (input: WebPageNodeInput): Partial<WebPage> => {
  const datePublished =
    input.datePublished instanceof Date
      ? input.datePublished.toISOString()
      : (input.datePublished ?? undefined);

  const dateModified =
    input.dateModified instanceof Date
      ? input.dateModified.toISOString()
      : (input.dateModified ?? undefined);

  const optimizedImageUrl = getSchemaImageUrl(input.image, 1200, 80);

  const speakable: SpeakableSpecification | undefined = input.speakable
    ? {
        '@type': 'SpeakableSpecification',
        xpath: [
          '/html/head/title',
          "/html/head/meta[@name='description']/@content",
        ],
      }
    : undefined;

  const breadcrumb = input.breadcrumbUrl
    ? { '@id': `${input.breadcrumbUrl}#BreadcrumbList` }
    : undefined;

  return cleanObject<Partial<WebPage>>({
    '@id': `${input.url}#WebPage`,
    url: input.url,
    name: input.name ?? undefined,
    description: input.description ?? undefined,
    inLanguage: input.inLanguage ?? undefined,
    datePublished,
    dateModified,
    image: optimizedImageUrl ?? ensureAbsoluteUrl(input.image),
    isPartOf: { '@id': webSiteId },
    sourceOrganization: { '@id': organizationId },
    breadcrumb: breadcrumb as WebPage['breadcrumb'],
    keywords: input.keywords?.length
      ? (input.keywords as WebPage['keywords'])
      : undefined,
    speakable: speakable as WebPage['speakable'],
  });
};

/**
 * Build a per-page WebPage node from a Contentful PageResponseDto, populating
 * fields from the page's SEO metadata when available. The caller is responsible
 * for supplying the canonical URL (including locale segment and microsite host)
 * so the node aligns with the canonical link tag emitted by `getMetadataFromPage`.
 */
export const buildWebPageNodeFromPage = (
  data: PageResponseDto,
  options: {
    url: string;
    locale?: string;
    breadcrumbUrl?: string | null;
    keywords?: DefinedTerm[];
  },
): Partial<WebPage> =>
  buildWebPageNode({
    url: options.url,
    name: data.seoTitle ?? data.pageTitle ?? undefined,
    description:
      data.seoMetaDescription ??
      data.summary ??
      data.shareDescription ??
      undefined,
    inLanguage: options.locale,
    image: data.shareImage?.image?.url ?? null,
    breadcrumbUrl: options.breadcrumbUrl,
    keywords: options.keywords,
    speakable: true,
  });

/**
 * Entity-disambiguation keywords for the organization, linking the brand terms
 * to the canonical Wikipedia entity. Intended for the home page WebPage node.
 */
export const buildOrganizationKeywords = (): DefinedTerm[] =>
  [
    {
      '@type': 'DefinedTerm',
      name: 'Compassion',
      sameAs: generalConfig.socials.wikipedia,
    },
    {
      '@type': 'DefinedTerm',
      name: generalConfig.publisher.name,
      sameAs: generalConfig.socials.wikipedia,
    },
  ] as DefinedTerm[];

export const buildDonateActionSchema = (fund?: Partial<FundProps>): object => ({
  agent: {
    '@type': 'Organization',
    name: generalConfig.publisher.name,
    url: generalConfig.domain.compassion,
  },
  recipient: {
    '@type': 'Organization',
    name: fund?.fundTitle || generalConfig.publisher.name,
    url: generalConfig.domain.compassion,
    sameAs: Object.values(generalConfig.socials),
  },
  target: {
    '@type': 'EntryPoint',
    url: generalConfig.domain.compassion,
  },
  // Only attach a MonetaryAmount when a specific fund is in context; the
  // generic gift-catalog/donate landing pages have no single amount range.
  ...(fund
    ? {
        object: {
          '@type': 'MonetaryAmount',
          minValue:
            fund.minOneTimeAmount ||
            fund.suggestedAmount ||
            fund.suggestedAmountsOneTime?.[0].value ||
            generalConfig.donation.amountMax,
          maxValue:
            fund.donationLimitTotal ||
            fund.suggestedAmountsOneTime?.at(-1)?.value ||
            generalConfig.donation.amountMax,
          currency: 'USD',
        },
      }
    : {}),
});
